/**
 * API Sequence Agent — Single Log Entry
 *
 * @module ui/components/node-config/ApiSequenceLogEntry
 *
 * Presentational component for a single line in the agent log stream.
 */

import { CheckCircle, XCircle, Info } from '@phosphor-icons/react';
import { Text, Spinner, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
type LogEntryIcon = 'spinner' | 'success' | 'error' | 'info';

type LogEntryProps = {
  icon: LogEntryIcon;
  text: string;
  detail?: string;
  badge?: { label: string; variant: 'positive' | 'negative' | 'neutral' };
};

const iconMap: Record<LogEntryIcon, React.ReactNode> = {
  spinner: <Spinner size="xs" className="text-blue-400 shrink-0" />,
  success: <CheckCircle size={14} weight="fill" className="text-green-500 shrink-0" />,
  error: <XCircle size={14} weight="fill" className="text-red-500 shrink-0" />,
  info: <Info size={14} className="text-muted-foreground shrink-0" />,
};

export function ApiSequenceLogEntry({ icon, text, detail, badge }: LogEntryProps) {
  return (
    <div className="flex items-start gap-2 py-1 px-2">
      <span className="mt-0.5">{iconMap[icon]}</span>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Text size="sm" className="text-foreground truncate">
          {text}
        </Text>
        {detail && (
          <Text size="xs" className="text-muted-foreground shrink-0">
            {detail}
          </Text>
        )}
        {badge && (
          <TagBadge name={badge.label} color={badgeVariantToTagColor(badge.variant === 'positive' ? 'success' : badge.variant === 'negative' ? 'destructive' : 'outline')} hideDot size="sm" className="shrink-0" />
        )}
      </div>
    </div>
  );
}
